import React from 'react';

import { gql, useLazyQuery } from '@apollo/client';
import { Refresh } from '@mui/icons-material';
import type { StackProps } from '@mui/material';
import { CardContent, IconButton, Stack, Typography, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { debounce, forEach, get, isEmpty, isNil, map, partition, sortBy, uniqBy } from 'lodash';
import { CardLevel, CashbackInfo, LoadingSpinner, RestaurantTitle } from '../../../components';
import useRestaurantCashbacks from '../../../components/use_restaurant_cashbacks';
import type { RestaurantType } from '../../../contexts/app_context';
import { AppContext } from '../../../contexts/app_context';
import { i18n } from '../../../i18n/i18n';
import DateJobsCard from './date_jobs_card';
import PeriodJobCard from './period_jobs_card';
import useLazyQueryDateJobs from './use_lazy_query_date_jobs';

import type { Job, PeriodJob } from '../../../graphql/graphql';

export type RestaurantDateJobsListProps = {
  restaurant: RestaurantType;
  outerStackProps?: StackProps;
};

const incomingJobsGql = gql(`
  query incomingJobs($restaurantId: ID) {
    incomingJobs(restaurantId: $restaurantId) {
      date
    }
  }
`);

type DateJobType = { isPeriod: false; scheduleDate: string; jobs: Job[] } | { isPeriod: true; periodJob: PeriodJob };

export const RestaurantDateJobsList = React.forwardRef<HTMLDivElement, RestaurantDateJobsListProps>(
  ({ restaurant, outerStackProps }, ref) => {
    const innerRef = React.useRef<HTMLDivElement>(null);
    React.useImperativeHandle(ref, () => innerRef.current!, []);

    const theme = useTheme();
    const { updateContextRestaurant } = React.useContext(AppContext);

    const [dateJobs, setDateJobs] = React.useState<DateJobType[]>([]);
    const [fetchDateJobs, { loading: dateJobsLoading }] = useLazyQueryDateJobs({
      queryOptions: {
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
      },
    });

    // Fetch and group normal jobs and period jobs
    const fetchDateJobsHandler = React.useCallback(
      async (data?: { incomingJobs: { date: string }[] }) => {
        const dates = map(data?.incomingJobs, 'date');

        const promises = map(dates, (date) => fetchDateJobs({ variables: { date, restaurantId: restaurant.id } }));
        const responses = await Promise.all(promises);

        let nextJobs: DateJobType[] = [];
        forEach(responses, ({ data: dateJobData }) => {
          const [normalJobs, periodJobs] = partition(dateJobData?.jobs, (job) => isNil(job.periodJob));

          if (!isEmpty(normalJobs)) {
            const scheduleDate = get(normalJobs, [0, 'scheduleDate'], '');
            nextJobs.push({ isPeriod: false, scheduleDate, jobs: normalJobs });
          }

          if (!isEmpty(periodJobs)) {
            forEach(periodJobs, (periodJob) => {
              nextJobs.push({ isPeriod: true, periodJob: periodJob.periodJob! });
            });
          }
        });

        nextJobs = uniqBy(nextJobs, (job) => (job.isPeriod ? job.periodJob.id : job.scheduleDate));
        nextJobs = sortBy(nextJobs, (job) =>
          job.isPeriod ? dayjs(job.periodJob.periodStartAt) : dayjs(job.scheduleDate),
        );

        setDateJobs(nextJobs);
      },
      [fetchDateJobs, restaurant.id],
    );

    const isFirstLoaded = React.useRef(false);
    const [cashbackSkip, setCashbackSkip] = React.useState(true);

    const [fetchData, { data, loading, networkStatus, refetch }] = useLazyQuery(incomingJobsGql, {
      variables: { restaurantId: restaurant.id },
      fetchPolicy: 'cache-and-network', // Return cached data immediately if exist while new data is fetching
      notifyOnNetworkStatusChange: true,
      onCompleted: fetchDateJobsHandler,
    });
    const { cashbacks, refetch: refetchCashbacks } = useRestaurantCashbacks(restaurant.id, {
      skip: cashbackSkip,
    });

    React.useEffect(() => {
      const callback = debounce((event) => {
        if (!isFirstLoaded.current && event[0].isIntersecting) {
          isFirstLoaded.current = true;
          setCashbackSkip(false);
          fetchData();
        }
      }, 250);

      const observer = new IntersectionObserver(callback, { threshold: 0.25 });
      if (innerRef.current) {
        observer.observe(innerRef.current);
      }

      return () => {
        if (innerRef.current) {
          observer.unobserve(innerRef.current);
        }
      };
    }, [fetchData]);

    // data is return immediately if in cache, loading indicate is fetching from network or not
    const isInitialLoading = loading && isNil(data);
    // Check is refetching from networkStatus === 4
    // 1 is first fetch on mount, 4 is refetching called, 7 is idle
    const isRefetching = networkStatus === 4;
    // Show loading spinner if data is not in cache (first load) or refetching
    const showLoading = isInitialLoading || isRefetching || dateJobsLoading;

    const refreshHandler = React.useCallback(async () => {
      await Promise.all([refetch(), refetchCashbacks()]);
    }, [refetch, refetchCashbacks]);

    React.useEffect(() => {
      if (loading || !isFirstLoaded.current) {
        return;
      }

      updateContextRestaurant(restaurant.id, (prevRestaurant) => ({
        ...prevRestaurant,
        isHiring: !isEmpty(data?.incomingJobs),
      }));
    }, [loading, data?.incomingJobs]);

    return (
      <Stack
        ref={innerRef}
        flexShrink={0}
        border={1}
        borderRadius={1}
        borderColor={theme.palette.divider}
        {...outerStackProps}
      >
        <Stack paddingX={1} paddingTop={0.75}>
          <Stack flexDirection="row" alignItems="center" justifyContent="flex-end" gap={1}>
            <IconButton size="small" color="info" onClick={refreshHandler}>
              <Refresh />
            </IconButton>
          </Stack>

          <Stack flexDirection="row" alignItems="center" height={64}>
            <RestaurantTitle
              imageUri={restaurant.frontPhotoUri}
              name={restaurant.name}
              branchName={restaurant.branchName}
            />
          </Stack>
        </Stack>
        {showLoading ? (
          <LoadingSpinner />
        ) : (
          <Stack flex={1} sx={{ overflowY: 'scroll', scrollPadding: '16px' }}>
            <CashbackInfo cashbacks={cashbacks} alertProps={{ sx: { marginX: 0.75 } }} />

            <Stack gap={1} padding={0.75}>
              {isEmpty(dateJobs) ? (
                <CardLevel variant="outlined" level={1}>
                  <CardContent>
                    <Typography variant="subtitle2" textAlign="center" sx={{ opacity: 0.6 }}>
                      {i18n.t('pages.jobs.no_jobs')}
                    </Typography>
                  </CardContent>
                </CardLevel>
              ) : (
                map(dateJobs, (dateJob) =>
                  dateJob.isPeriod ? (
                    <PeriodJobCard key={dateJob.periodJob.id} periodJob={dateJob.periodJob} />
                  ) : (
                    <DateJobsCard
                      key={dateJob.scheduleDate}
                      jobs={dateJob.jobs}
                      date={dateJob.scheduleDate}
                      restaurantId={restaurant.id}
                    />
                  ),
                )
              )}
            </Stack>
          </Stack>
        )}
      </Stack>
    );
  },
);

export default RestaurantDateJobsList;
